import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/lib/auth';

export function useNewGroupSessionCount() {
  const { user } = useAuth();
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!user) {
      setCount(0);
      return;
    }

    const fetchNewGroupSessions = async () => {
      // First get the user's last viewed timestamp
      const { data: profile } = await supabase
        .from('profiles')
        .select('group_sessions_last_viewed_at')
        .eq('id', user.id)
        .single();

      const lastViewedAt = profile?.group_sessions_last_viewed_at;

      // Build query for upcoming group sessions
      let query = supabase
        .from('sessions')
        .select('id, created_at', { count: 'exact', head: true })
        .eq('session_type', 'group_conversation')
        .eq('status', 'upcoming')
        .gt('session_date', new Date().toISOString());

      // If user has viewed before, only count sessions created after that
      if (lastViewedAt) {
        query = query.gt('created_at', lastViewedAt);
      }

      const { count: newCount, error } = await query;

      if (error) {
        console.error('Error fetching new group sessions:', error);
        return;
      }

      setCount(newCount || 0);
    };

    fetchNewGroupSessions();

    // Subscribe to changes on sessions table
    const channel = supabase
      .channel(`group-sessions-changes-${user.id}-${Math.random().toString(36).slice(2, 10)}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'sessions',
        },
        (payload) => {
          // Only refetch if it's a group_conversation session
          const newRecord = payload.new as { session_type?: string } | undefined;
          const oldRecord = payload.old as { session_type?: string } | undefined;
          if (
            newRecord?.session_type === 'group_conversation' ||
            oldRecord?.session_type === 'group_conversation'
          ) {
            fetchNewGroupSessions();
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const markGroupSessionsAsSeen = async () => {
    if (!user) return;

    const { error } = await supabase
      .from('profiles')
      .update({ group_sessions_last_viewed_at: new Date().toISOString() })
      .eq('id', user.id);

    if (error) {
      console.error('Error marking group sessions as seen:', error);
      return;
    }

    setCount(0);
  };

  return { count, markGroupSessionsAsSeen };
}
